import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { invoiceKeys, type SalesInvoice } from './invoices-api';
import type { BadgeTone } from '../components/ui';

/**
 * Peppol e-fatura ucunun panel tarafındaki SÖZLEŞMESİ.
 *
 * Burası şu üç dosyanın AYNASIDIR ve hiçbir alan adı, durum değeri ya da hata
 * kodu tahmin edilerek yazılmadı:
 *   · `apps/api/src/modules/einvoice/einvoice.controller.ts` (yollar, yöntemler)
 *   · `apps/api/src/modules/einvoice/einvoice.service.ts`    (görünüm tipleri)
 *   · `apps/api/src/entities/einvoice-document.entity.ts`    (durum sözlükleri)
 *
 * ── EKRANIN CEVAPLADIĞI TEK SORU ──────────────────────────────────────────
 * "Yasal olarak iletilmesi gereken ama iletilememiş belgem var mı?" Bu, sunucu
 * tarafında `einvoiceRequired = true AND einvoiceDocumentId IS NULL`
 * yüklemidir (`EinvoiceTransmissionView.obligationOpen`). Panel aynı yüklemi
 * kullanır; başka bir "gönderildi mi" ölçütü uydurmak, hiç iletilmemiş
 * faturaları iletilmiş göstermenin en kısa yoludur.
 *
 * ── İKİ SÖZCÜK BİRBİRİNİN YERİNE KULLANILAMAZ ─────────────────────────────
 * `SUBMITTED` sağlayıcının VERDİĞİ SÖZDÜR ("taşımaya aldım"), `DELIVERED` alıcı
 * erişim noktasının ONAYIDIR. Aralarında saatler geçebilir. Panelde ikisini
 * "gönderildi" diye birleştirmek, sağlayıcının kuyruğunda bekleyen bir belgeyi
 * teslim edilmiş göstermek demektir — bu yüzden her durumun ekranda GÖRÜNEN
 * bir ANLAM cümlesi vardır (`EINVOICE_STATUS_MEANINGS`).
 *
 * ── PARA ──────────────────────────────────────────────────────────────────
 * Tutarlar `string`tir ve panel hiçbir yerde aritmetik yapmaz; `money()` ile
 * yalnızca biçimlenir (`moneyPlain()` MAKİNE içindir, ekranda kullanılmaz).
 */

// ──────────────────────────────────────────────────────────────── sözlükler
//
// ⚠ Bu diziler sunucunun sözlükleriyle BİREBİR aynı olmak zorunda. Sürüklenmeyi
// `einvoice-api.spec.ts` kapıda tutuyor: sunucu kaynağını `?raw` ile okuyup
// buradaki kopyayla karşılaştırır. İki kopyanın var olma sebebi, panelin bugün
// API'nin `dist`ini import edememesidir (aynı gerekçe `invoices-api.ts`te de
// yazılı).

/** Taşıma durumu — alıcının İŞ yanıtını ANLATMAZ (o ayrı alanlardadır). */
export const EINVOICE_DOCUMENT_STATUSES = [
  'PREPARED',
  'SENDING',
  'SUBMITTED',
  'DELIVERED',
  'RETRYABLE_ERROR',
  'REJECTED',
  'SENT_MANUALLY',
  'ABANDONED',
] as const;
export type EinvoiceDocumentStatus = (typeof EINVOICE_DOCUMENT_STATUSES)[number];

export const EINVOICE_NETWORKS = ['TEST', 'LIVE'] as const;
export type EinvoiceNetwork = (typeof EINVOICE_NETWORKS)[number];

export const EINVOICE_TRANSPORT_KINDS = ['API', 'MANUAL'] as const;
export type EinvoiceTransportKind = (typeof EINVOICE_TRANSPORT_KINDS)[number];

export const PEPPOL_MODES = ['OFF', 'TEST', 'LIVE'] as const;
export type PeppolMode = (typeof PEPPOL_MODES)[number];

export const EINVOICE_LOOKUP_RESULTS = ['FOUND', 'NOT_REGISTERED', 'UNAVAILABLE'] as const;
export type EinvoiceLookupResult = (typeof EINVOICE_LOOKUP_RESULTS)[number];

/** Satıra YAZILABİLEN arıza sınıfları — `CONFIG` bilerek yok (ortam arızasıdır). */
export const EINVOICE_ROW_FAILURE_CLASSES = ['DOCUMENT', 'RECIPIENT', 'TRANSPORT'] as const;
export type EinvoiceFailureClass = (typeof EINVOICE_ROW_FAILURE_CLASSES)[number];

export const EINVOICE_BUSINESS_RESPONSE_STATUSES = ['ACCEPTED', 'REJECTED', 'UNDER_QUERY'] as const;
export type EinvoiceBusinessResponseStatus = (typeof EINVOICE_BUSINESS_RESPONSE_STATUSES)[number];

/**
 * MEŞRU GEÇİŞLER — sunucudaki `EINVOICE_TRANSITIONS`ın aynası.
 *
 * ⚠ Panel bunu yalnızca DÜĞMEYİ GİZLEMEK için okur; asıl kapı sunucudadır ve
 * orada da tek kaynaktır. Yine de kopyanın doğru olması gerekir: yanlış bir
 * kopya ya var olmayan bir eylemi teklif eder (operatör tıklar,
 * `EINVOICE_INVALID_STATE` alır) ya da meşru bir eylemi gizler — ikincisi daha
 * kötüdür, çünkü reddedilmiş bir belge yeniden gönderilebilir olduğu hâlde
 * gönderilemez görünür ve yasal yükümlülük sessizce açık kalır.
 */
export const EINVOICE_TRANSITIONS: Readonly<
  Record<EinvoiceDocumentStatus, readonly EinvoiceDocumentStatus[]>
> = {
  PREPARED: ['SENDING', 'SENT_MANUALLY', 'ABANDONED'],
  SENDING: ['SUBMITTED', 'DELIVERED', 'RETRYABLE_ERROR', 'REJECTED'],
  SUBMITTED: ['DELIVERED', 'REJECTED'],
  DELIVERED: [],
  RETRYABLE_ERROR: ['SENDING', 'REJECTED', 'SENT_MANUALLY', 'ABANDONED'],
  REJECTED: ['SENDING', 'SENT_MANUALLY', 'ABANDONED'],
  SENT_MANUALLY: [],
  ABANDONED: [],
};

/** Geçiş tablosundan TÜRETİLİR — elle yazılmaz (iki liste bir gün ayrışır). */
function statusesLeadingTo(target: EinvoiceDocumentStatus): EinvoiceDocumentStatus[] {
  return EINVOICE_DOCUMENT_STATUSES.filter((from) => EINVOICE_TRANSITIONS[from].includes(target));
}

export const SENDABLE_STATUSES = statusesLeadingTo('SENDING');
export const MANUAL_MARKABLE_STATUSES = statusesLeadingTo('SENT_MANUALLY');
export const ABANDONABLE_STATUSES = statusesLeadingTo('ABANDONED');

/** Mührü yazmaya hak kazandıran durumlar — yükümlülüğün kapandığı küme. */
export const EINVOICE_SUCCESS_STATUSES: readonly EinvoiceDocumentStatus[] = [
  'SUBMITTED',
  'DELIVERED',
  'SENT_MANUALLY',
];

// ───────────────────────────────────────────────────────────── sunucu tipleri

/**
 * `einvoice_documents` satırı — JSON hâli.
 *
 * ⚠ Zaman damgaları ISO `string`tir (TypeORM `timestamptz` → JSON dizesi);
 * `Date` değil. Biçimleme `format.ts#dateTime` ile yapılır (Brüksel saati).
 */
export interface EinvoiceDocument {
  id: string;
  salesInvoiceId: string;
  network: EinvoiceNetwork;
  providerName: string | null;
  transportKind: EinvoiceTransportKind;
  senderParticipantId: string;
  recipientParticipantId: string;

  /** Arşivlenmiş baytların proje köküne GÖRELİ yolu. */
  xmlStoragePath: string;
  xmlSha256: string;
  xmlByteLength: number;
  ublCustomizationId: string;
  mapperVersion: string;
  preparedAt: string;

  status: EinvoiceDocumentStatus;
  statusChangedAt: string;
  attemptCount: number;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  sendingSince: string | null;

  submittedAt: string | null;
  deliveredAt: string | null;
  providerMessageId: string | null;

  manualSentAt: string | null;
  manualSentByUserId: string | null;
  manualReference: string | null;

  supersededAt: string | null;
  supersededByDocumentId: string | null;
  supersedeReason: string | null;

  failureClass: EinvoiceFailureClass | null;
  failureCode: string | null;
  /**
   * Sağlayıcının ret metni, BİREBİR.
   *
   * ⚠ BU METNİN OKUNABİLDİĞİ TEK YER, `GET /einvoice/invoices/:id`in 200
   * yanıtıdır: geçici arıza 503 döner ve `AllExceptionsFilter` 5xx'te
   * `message`i DÜŞÜRÜR. İçindeki kural kodu (`BR-CO-13` gibi) operatörün kendi
   * doğrulayıcımızın bulgularıyla eşleştirebileceği tek anahtardır — ekranda
   * gösterilmezse hiçbir yerde görünmez.
   */
  failureMessage: string | null;
  failedAt: string | null;

  recipientLookupAt: string | null;
  recipientLookupResult: EinvoiceLookupResult | null;

  businessResponseStatus: EinvoiceBusinessResponseStatus | null;
  businessResponseCodeRaw: string | null;
  businessResponseReason: string | null;
  businessResponseAt: string | null;

  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** `GET /einvoice/invoices/:id` — panelin tek okuma ucu. */
export interface EinvoiceTransmissionView {
  invoiceId: string;
  documentNumber: string | null;
  invoiceStatus: string;
  einvoiceRequired: boolean;
  einvoiceObligationBasis: string | null;
  /** MÜHÜR — yükümlülüğün karşılandığı belge. */
  einvoiceDocumentId: string | null;
  /** HUKUKİ ALARM: yükümlülüğü var ve hâlâ iletilememiş. */
  obligationOpen: boolean;
  /** ⚠ AYARIN O ANKİ hâli; satıra donmuş `network` ile karıştırılmaz. */
  mode: PeppolMode;
  network: EinvoiceNetwork;
  transportKind: EinvoiceTransportKind;
  providerName: string;
  /** Geçerli ağdaki AKTİF (supersede edilmemiş) satır. */
  active: EinvoiceDocument | null;
  /** Supersede edilmişler ve DİĞER ağın satırları dâhil, hepsi. */
  documents: EinvoiceDocument[];
}

export interface EinvoicePrepareView {
  document: EinvoiceDocument;
  /** Baytlar zaten hazırdı; yeniden üretilmedi. */
  reused: boolean;
}

export interface PeppolManualHandoff {
  xmlRelativePath: string | null;
  network: EinvoiceNetwork;
  /** Sunucunun ürettiği TÜRKÇE talimat — panel onu OLDUĞU GİBİ gösterir. */
  instruction: string;
}

export interface EinvoiceSendView {
  /**
   * ⚠ `MANUAL_REQUIRED` bir HATA DEĞİL, bir BAŞARI DA DEĞİL: "baytlar hazır,
   * taşıma insana düştü". Yanıt 200'dür ama `document.status` `PREPARED`
   * kalır. Panel `outcome`a bakmadan "gönderildi" yazarsa, hiç iletilmemiş
   * faturalar gönderilmiş görünür.
   */
  outcome: 'SUBMITTED' | 'SENT_MANUALLY' | 'MANUAL_REQUIRED';
  document: EinvoiceDocument;
  handoff: PeppolManualHandoff | null;
  /** İşlemi durdurmayan ama söylenmesi gereken şeyler — KOD listesi. */
  warnings: string[];
}

// ────────────────────────────────────────────────────────────────── etiketler

export const EINVOICE_STATUS_LABELS: Record<EinvoiceDocumentStatus, string> = {
  PREPARED: 'Hazırlandı',
  SENDING: 'Gönderiliyor',
  SUBMITTED: 'Sağlayıcı aldı',
  DELIVERED: 'Ağ teslim etti',
  RETRYABLE_ERROR: 'Geçici arıza',
  REJECTED: 'Reddedildi',
  SENT_MANUALLY: 'Elle gönderildi',
  ABANDONED: 'Vazgeçildi',
};

/**
 * Durumun ANLAMI — etiket yetmez, çünkü asıl soru "gönderdim mi"dir.
 *
 * ⚠ `SUBMITTED` ile `DELIVERED` arasındaki farkın ekranda YAZILI olması
 * zorunludur: ikisi de yeşil bir rozetle gösterilseydi, sağlayıcının
 * kuyruğunda bekleyen (ve orada başarısız olabilecek) bir belge teslim
 * edilmiş sanılırdı.
 */
export const EINVOICE_STATUS_MEANINGS: Record<EinvoiceDocumentStatus, string> = {
  PREPARED:
    'Baytlar üretildi, doğrulandı ve arşivlendi. AĞA HİÇ ÇIKILMADI — belge henüz iletilmedi.',
  SENDING:
    'İstek uçuşta; sonucu BİLİNMİYOR. Kör yeniden deneme yapılmaz: sağlayıcı belgeyi almış olabilir ve ikinci deneme alıcıda ikinci bir borç kaydı doğurur.',
  SUBMITTED:
    'Sağlayıcı belgeyi taşımaya ALDI. Bu bir SÖZDÜR, TESLİM DEĞİLDİR: alıcıya ulaşıp ulaşmadığı henüz bilinmiyor.',
  DELIVERED: 'Alıcı erişim noktası teslimi ONAYLADI. Taşıma tamamlandı ve bu durum geri alınmaz.',
  RETRYABLE_ERROR:
    'Geçici arıza (5xx / zaman aşımı / ağ). Belge HÂLÂ İLETİLMEDİ; yeniden denenebilir.',
  REJECTED:
    'Belge ya da alıcı kaynaklı ret. Otomatik yeniden deneme YOK; sebebi giderildiğinde AYNI baytlarla yeniden gönderilebilir.',
  SENT_MANUALLY:
    'Baytları bir İNSAN taşıdı (portala yükledi). Bir beyandır ve TERMİNALDİR — geri alınamaz.',
  ABANDONED:
    'Hiç gönderilmedi, operatör vazgeçti. TERMİNAL. Yasal yükümlülük bu belgeyle karşılanmadı.',
};

/**
 * Rozet rengi.
 *
 * ⚠ `SUBMITTED` bilerek `ok` DEĞİL: yeşil, operatörün "bitti" diye okuduğu
 * renktir ve sağlayıcının sözü henüz teslim değildir.
 */
export const EINVOICE_STATUS_TONES: Record<EinvoiceDocumentStatus, BadgeTone> = {
  PREPARED: 'neutral',
  SENDING: 'brand',
  SUBMITTED: 'warn',
  DELIVERED: 'ok',
  RETRYABLE_ERROR: 'warn',
  REJECTED: 'danger',
  SENT_MANUALLY: 'ok',
  ABANDONED: 'danger',
};

export const EINVOICE_NETWORK_LABELS: Record<EinvoiceNetwork, string> = {
  TEST: 'TEST ağı',
  LIVE: 'LIVE (gerçek) ağ',
};

export const PEPPOL_MODE_LABELS: Record<PeppolMode, string> = {
  OFF: 'KAPALI',
  TEST: 'TEST',
  LIVE: 'LIVE (gerçek)',
};

export const PEPPOL_MODE_MEANINGS: Record<PeppolMode, string> = {
  OFF: 'Otomatik gönderim yapılmaz. Tek meşru yol: hazırla → XML indir → portala yükle → "elle gönderildi" işaretle.',
  TEST: 'Gönderimler TEST ağına gider. ⚠ TEST ağına iletilen bir belge YASAL OLARAK İLETİLMİŞ DEĞİLDİR.',
  LIVE: 'Gönderimler gerçek ağa gider.',
};

export const EINVOICE_TRANSPORT_KIND_LABELS: Record<EinvoiceTransportKind, string> = {
  API: 'Sağlayıcı API’si',
  MANUAL: 'Elle taşıma (portal)',
};

export const EINVOICE_FAILURE_CLASS_LABELS: Record<EinvoiceFailureClass, string> = {
  DOCUMENT: 'Belge reddi',
  RECIPIENT: 'Alıcı ağda bulunamadı',
  TRANSPORT: 'Taşıma arızası',
};

/**
 * ⚠ Üç değerli, boolean DEĞİL. "Alıcı ağa kayıtlı değil" (dünya hakkında bir
 * olgu, operatör aksiyonu gerektirir) ile "sorgu cevap vermedi" (bugün hakkında
 * bir olgu, geçici) aynı kovaya konsaydı, bir DNS kesintisinde operatör tüm
 * faturaları elle göndermeye başlardı.
 */
export const EINVOICE_LOOKUP_RESULT_LABELS: Record<EinvoiceLookupResult, string> = {
  FOUND: 'Alıcı ağda bulundu',
  NOT_REGISTERED: 'Alıcı ağa kayıtlı DEĞİL',
  UNAVAILABLE: 'Arama cevap vermedi (geçici)',
};

/**
 * Alıcının İŞ yanıtı — TAŞIMAYI DEĞİŞTİRMEZ.
 *
 * ⚠ Bir iş reddi belgenin İLETİLMEDİĞİ anlamına GELMEZ: belge hukuken teslim
 * edilmiştir, KDV doğmuştur ve çare TİCARİDİR (kredi notu). Ekranda taşıma
 * durumuyla aynı yerde gösterilseydi operatör teslim edilmiş bir faturayı
 * yeniden gönderirdi.
 */
export const EINVOICE_BUSINESS_RESPONSE_LABELS: Record<EinvoiceBusinessResponseStatus, string> = {
  ACCEPTED: 'Alıcı kabul etti',
  REJECTED: 'Alıcı ticari olarak reddetti',
  UNDER_QUERY: 'Alıcı sorguladı',
};

/** `EINVOICE_OBLIGATION_BASES` — sunucuda bugün tek değerli, kapalı sözlük. */
export const EINVOICE_OBLIGATION_BASIS_LABELS: Record<string, string> = {
  ART_53_2BIS: 'Yurtiçi B2B — WBTW md. 53 §2bis',
};

// ─────────────────────────────────────────────────────────────────── yardımcı

/**
 * Belgenin kaç gündür iletilmemiş olduğu — `issueDate` (takvim GÜNÜ) üzerinden.
 *
 * ⚠ `issueDate` bir takvim günüdür (`YYYY-AA-GG`), an değil. `new Date(...)`
 * onu UTC gece yarısı sayar; iki günün FARKINI aldığımız için saat dilimi
 * kayması sonucu değiştirmez, ama bu yüzden burada saat aritmetiği yapılmaz.
 */
export function daysOpen(issueDate: string, today: Date = new Date()): number | null {
  const issued = Date.parse(`${issueDate}T00:00:00Z`);
  if (Number.isNaN(issued)) return null;
  const now = Date.parse(`${today.toISOString().slice(0, 10)}T00:00:00Z`);
  return Math.max(0, Math.round((now - issued) / 86_400_000));
}

/*
 * ⚠ `hasOpenObligation` KALDIRILDI — ve bu bir sadeleştirme değil, bir
 * DOKTRİN gereği.
 *
 * O fonksiyon sunucudaki `obligationOpen` yükleminin panel KOPYASIYDI ve
 * kuyruk istemcide süzüldüğü sürece gerekliydi. Kuyruk artık sunucudan
 * geliyor (`GET /einvoice/queue`), yani kopya hiçbir yerden okunmuyordu.
 *
 * Okunmayan bir kopya silinmezse iki şey olur: bir gün yüklem sunucuda
 * genişler (örneğin kamu kurumu kapsama alınır) ve panel kopyası ESKİ
 * kuralı anlatmaya devam eder; ya da biri onu "hazır dururken" yeniden
 * kullanır ve panel, sunucunun göstereceğinden FARKLI bir liste çizer.
 * Hukuki bir yüklemin iki kaynağı olamaz.
 */

// ────────────────────────────────────────────────────────────────── sorgular

export const einvoiceKeys = {
  all: ['einvoice'] as const,
  transmission: (invoiceId: string) => ['einvoice', 'transmission', invoiceId] as const,
};

function transmissionQuery(invoiceId: string) {
  return {
    queryKey: einvoiceKeys.transmission(invoiceId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      api.get<EinvoiceTransmissionView>(`/einvoice/invoices/${invoiceId}`, { signal }),
  };
}

export function useEinvoiceTransmission(invoiceId: string | null) {
  return useQuery({
    ...transmissionQuery(invoiceId ?? ''),
    enabled: invoiceId !== null,
  });
}

/**
 * Görünen satırların taşıma durumları — SATIR BAŞINA BİR İSTEK.
 *
 * ⚠ Bu bir tercih değil, bugünkü API'nin sınırı: taşıma satırlarını TOPLU
 * döndüren bir uç YOK (`einvoice.controller.ts` yalnız
 * `GET /einvoice/invoices/:id` sunuyor) ve sözleşme uydurulmaz. Sağlayıcı
 * seçilip liste ucu yazıldığında burası TEK bir isteğe iner.
 *
 * ⚠ Bu yüzden fan-out YALNIZ GÖRÜNEN SAYFAYA uygulanır ve sayfa boyu küçük
 * tutulur (`QUEUE_PAGE_SIZE`): kota sınırı 120 istek/dakikadır ve sayaçlar
 * SÜREÇ İÇİ bellektedir (PM2 `instances: 1`). Tüm kuyruğa fan-out yapan bir
 * "iyileştirme", operatörün kendi kovasını boşaltır ve ekran 429 döndürmeye
 * başlar — üstelik en çok belge birikmiş, yani en kritik anda.
 */
export function useEinvoiceTransmissions(invoiceIds: readonly string[]) {
  return useQueries({
    queries: invoiceIds.map((id) => transmissionQuery(id)),
    combine: (results) => {
      const byInvoice = new Map<string, EinvoiceTransmissionView>();
      results.forEach((r, i) => {
        const id = invoiceIds[i];
        if (id !== undefined && r.data) byInvoice.set(id, r.data);
      });
      return { byInvoice, isPending: results.some((r) => r.isPending) };
    },
  });
}

// ────────────────────────────────────────────────────────────── gönderim kuyruğu

/** Kuyruk sayfası — fan-out bununla sınırlıdır (bkz. `useEinvoiceTransmissions`). */
export const QUEUE_PAGE_SIZE = 20;

/**
 * Kuyruk artık SUNUCUDAN gelir.
 *
 * ⚠ ÖNCEDEN İSTEMCİDE TARANIYORDU ve bu, listeyi tam da en çok görülmesi
 * gereken yerde eksik bırakıyordu. Sunucuda "yükümlülüğü açık belgeler" ucu
 * yoktu ve `GET /invoices` yüklem süzgeci kabul etmiyordu, bu yüzden panel
 * sayfa sayfa tarayıp `hasOpenObligation` ile eliyordu. Tarama sınırlıydı
 * (400 belge) — ve taramanın DIŞINDA kalanlar EN ESKİ, yani en uzun süredir
 * iletilememiş, EN RİSKLİ belgelerdi.
 *
 * Kural tek satırlıktır (`einvoiceRequired AND einvoiceDocumentId IS NULL`)
 * ve `GET /einvoice/queue` onu veritabanında yanıtlar. Sıra da kaynağında
 * doğru: EN ESKİDEN yeniye.
 */
export interface EinvoiceQueue {
  /** Yükümlülüğü açık belgeler — EN ESKİSİ ÜSTTE. */
  items: SalesInvoice[];
  /** Yükümlülüğü açık TOPLAM belge sayısı (sayfalamadan bağımsız). */
  total: number;
}
export const einvoiceQueueKey = ['einvoice', 'queue'] as const;

export const einvoiceInboxKey = ['einvoice', 'inbox'] as const;

/**
 * GELEN KUTUSU satırı — üstveri, TUTAR DEĞİL.
 *
 * ⚠ Gelen belgede tutar/KDV gösterilmez: belge alınmış olmak onaylanmış olmak
 * değildir ve ekranda görünen bir rakam doğrulanmış sanılır. Rakamlar ancak
 * taslak alışa çevrildikten sonra, onay akışının içinde görünür.
 */
export interface IncomingDocument {
  id: string;
  receivedAt: string;
  network: string;
  providerName: string | null;
  contentType: string | null;
  payloadByteLength: number;
  payloadSha256: string;
  transportSenderId: string | null;
  documentKind: string;
  processingStatus: string;
  processingNote: string | null;
  /** `HMAC_SHA256` … imzalı teslimat · `OPERATOR_UPLOAD` elden yükleme. */
  verificationMethod: string;
}

export const INCOMING_VERIFICATION_LABELS: Record<string, string> = {
  HMAC_SHA256: 'imzalı teslimat',
  HMAC_SHA512: 'imzalı teslimat',
  QUERY_TOKEN: 'jetonlu teslimat',
  OPERATOR_UPLOAD: 'elden yüklendi',
};

export function useEinvoiceInbox(enabled = true) {
  return useQuery({
    queryKey: einvoiceInboxKey,
    enabled,
    queryFn: () =>
      api.get<{ items: IncomingDocument[]; total: number }>('/einvoice/inbox?limit=50'),
  });
}

/**
 * Tedarikçinin e-postayla gönderdiği UBL'i gelen kutusuna koyar.
 *
 * ⚠ GÖVDE HAM BAYTLARDIR, `FormData` DEĞİL (ek ve makbuz uçlarıyla aynı
 * sözleşme). Sunucu dosyayı ÖNCE okur: UBL değilse arşive hiç yazılmaz.
 */
export function useUploadIncomingUbl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      api.postRaw<{ status: 'STORED' | 'DUPLICATE'; documentId: string }>(
        '/einvoice/inbox/upload',
        file,
        file.type === '' ? 'application/xml' : file.type,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: einvoiceInboxKey }),
  });
}

/**
 * Gönderim bekleyenler — hukuki alarm listesi.
 *
 * ⚠ TASLAKLAR SUNUCUDA ELENİR. `einvoiceRequired` taslakta da hesaplanır
 * (`sales-invoice.service.ts#einvoiceObligation`) ama taslağın numarası
 * yoktur ve gönderilemez; uç `status` üzerinden DRAFT ve CANCELLED dışında
 * bırakır. Bu eleme daha önce panelde yapılıyordu çünkü sunucunun `status`
 * süzgeci tek değer alıyordu — artık kuralın kendisi sunucuda.
 */
export function useEinvoiceQueue(enabled: boolean) {
  return useQuery({
    queryKey: einvoiceQueueKey,
    enabled,
    queryFn: ({ signal }): Promise<EinvoiceQueue> =>
      api.get<EinvoiceQueue>(`/einvoice/queue?limit=${QUEUE_PAGE_SIZE}&offset=0`, { signal }),
  });
}
// ───────────────────────────────────────────────────────────────── mutasyonlar

/**
 * Bir faturanın taşıma verisini tazeler.
 *
 * ⚠ KUYRUK DA TAZELENİR: başarılı bir iletim faturaya MÜHÜR yazar
 * (`sales_invoices.einvoiceDocumentId`) ve o satır artık kuyrukta olmamalıdır.
 * Yalnız taşıma sorgusu geçersizlenseydi, iletilmiş bir belge listede
 * "bekliyor" olarak kalır ve operatör onu ikinci kez gönderirdi.
 *
 * ⚠ Geçersizleme SEÇİCİDİR: `einvoiceKeys.all` kökünü geçersizlemek, görünen
 * her satırın taşıma sorgusunu AYNI ANDA yeniden çalıştırırdı (kota kovası).
 */
function useTransmissionInvalidator() {
  const qc = useQueryClient();
  return (invoiceId: string) => {
    void qc.invalidateQueries({ queryKey: einvoiceKeys.transmission(invoiceId) });
    void qc.invalidateQueries({ queryKey: einvoiceQueueKey });
    void qc.invalidateQueries({ queryKey: invoiceKeys.all });
  };
}

/** Baytları üretir, doğrular, arşivler — AĞA ÇIKMAZ. `peppol_mode = OFF` iken de çalışır. */
export function usePrepareEinvoice() {
  const invalidate = useTransmissionInvalidator();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.post<EinvoicePrepareView>(`/einvoice/invoices/${invoiceId}/prepare`),
    onSuccess: (_res, invoiceId) => invalidate(invoiceId),
  });
}

/**
 * Gönderir ya da yeniden dener.
 *
 * ⚠ 200 dönmesi "iletildi" DEMEK DEĞİLDİR — `outcome`a bakılır. Elle taşıma
 * yolunda yanıt `MANUAL_REQUIRED` ve satır `PREPARED` kalır.
 */
export function useSendEinvoice() {
  const invalidate = useTransmissionInvalidator();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.post<EinvoiceSendView>(`/einvoice/invoices/${invoiceId}/send`),
    onSuccess: (_res, invoiceId) => invalidate(invoiceId),
  });
}

/**
 * "Portala yükledim" BEYANI.
 *
 * ⚠ GERİ ALINAMAZ ve bu şemada yazılıdır: `manualSentAt` / `manualSentByUserId`
 * / `manualReference` KİLİT kolonlardır (bir kez yazılır), `SENT_MANUALLY`
 * geçiş tablosunda TERMİNALDİR ve LIVE ağda faturaya MÜHÜR yazılır. Yanlış
 * işaretlenen bir belge hukuki alarm sorgusunda bir daha ASLA görünmez.
 */
export function useMarkSentManually() {
  const invalidate = useTransmissionInvalidator();
  return useMutation({
    mutationFn: ({ invoiceId, reference }: { invoiceId: string; reference: string }) =>
      api.post<EinvoiceSendView>(`/einvoice/invoices/${invoiceId}/mark-sent-manually`, {
        reference,
      }),
    onSuccess: (_res, vars) => invalidate(vars.invoiceId),
  });
}

/**
 * `POST /einvoice/invoices/:id/send-email` yanıtı.
 *
 * ⚠ Bu yol PEPPOL DIŞI kanaldır ve yalnız müşteri anlaştıysa açıktır; ekte UBL
 * (faturanın kendisi) ve PDF (okunur kopya) birlikte gider. Başarıda belge
 * `SENT_MANUALLY` olur: taşımayı gerçekten biz yaptık ve kanıt satıra yazıldı.
 */
export interface InvoiceEmailResult {
  outcome: 'SENT_BY_EMAIL';
  recipient: string;
  providerMessageId: string;
  document: EinvoiceDocument;
  warnings: string[];
}

export function useSendInvoiceEmail() {
  const invalidate = useTransmissionInvalidator();
  return useMutation({
    mutationFn: (invoiceId: string) =>
      api.post<InvoiceEmailResult>(`/einvoice/invoices/${invoiceId}/send-email`),
    onSuccess: (_res, invoiceId) => invalidate(invoiceId),
  });
}

/** Vazgeçme — TERMİNAL. Hiçbir kod yolu bunu otomatik yapmaz; karar insanındır. */
export function useAbandonEinvoice() {
  const invalidate = useTransmissionInvalidator();
  return useMutation({
    mutationFn: ({ invoiceId, reason }: { invoiceId: string; reason: string }) =>
      api.post<EinvoiceDocument>(`/einvoice/invoices/${invoiceId}/abandon`, { reason }),
    onSuccess: (_res, vars) => invalidate(vars.invoiceId),
  });
}

// ──────────────────────────────────────────────────────────────── XML indirme

/**
 * Arşivlenmiş UBL baytlarını indirir.
 *
 * ⚠ TESLİM EDİLMİŞ bir belgede sunucu 409 `EINVOICE_ALREADY_DELIVERED` döner ve
 * `acknowledgeResend=true` ister. Bu bir gürültü değil, bir KAPIDIR: elle
 * rejimde indirmek TESLİMATIN KENDİSİDİR ve ikinci bir yükleme alıcıda ikinci
 * bir borç kaydı doğurur. Panel bunu bir onay adımına çevirir; onayı otomatik
 * göndermek kapıyı yok etmek olurdu.
 */
export function downloadEinvoiceXml(invoiceId: string, acknowledgeResend = false) {
  const query = acknowledgeResend ? '?acknowledgeResend=true' : '';
  return api.file(`/einvoice/invoices/${invoiceId}/xml${query}`);
}

/**
 * Baytları kullanıcının diskine verir.
 *
 * ⚠ Nesne URL'si HER ZAMAN serbest bırakılır: bırakılmazsa baytlar sekme
 * kapanana kadar bellekte kalır ve operatör gün boyunca onlarca belge
 * indirebilir.
 */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Tıklama eşzamansız başlar; hemen iptal etmemek için bir tur beklenir.
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }
}
